// src/plfTables.js
// Lender PLF tables for proprietary products, read off the lender's own
// pricing software by Reece (10/1/2026). He entered a 1/1/YYYY birthday for
// each birth year; lenders price on the age at the NEAREST birthday, so on
// 10/1/2026 a 1/1/YYYY borrower is (2027 − YYYY). Tables are stored by that
// nearest age, so they stay right as time passes.
//
// A table belongs to one product tier at the rate it was read at. If the
// sheet rate later drifts a little, the table is scaled by the generic
// curve's rate slope; further than MAX_RATE_DRIFT and the tier falls back
// to the estimate (and says so on the card).

const READ_ON_YEAR=2027; // 1/1/YYYY birthdays on 10/1/2026 → nearest age = 2027 − YYYY
const MAX_RATE_DRIFT=0.5;

function byBirthYear(pairs){
  const out={};
  pairs.forEach(function(p){out[READ_ON_YEAR-p[0]]=p[1];});
  return out;
}

// Lowest-rate fixed: HomeSafe Standard 8.49 and SmartFi Choice Fixed Saver (same PLFs).
const LOW_FIXED=byBirthYear([
  [1935,.529],[1939,.528],[1940,.527],[1941,.523],[1942,.520],[1943,.510],[1944,.504],[1945,.490],
  [1946,.476],[1947,.465],[1948,.454],[1949,.442],[1950,.437],[1951,.427],[1952,.417],[1953,.407],
  [1954,.396],[1955,.388],[1956,.380],[1957,.372],[1958,.364],[1959,.358],[1960,.352],[1961,.346],
  [1962,.340],[1963,.336],[1964,.332],[1965,.328],[1966,.306],[1967,.303],[1968,.299],[1969,.296],
  [1970,.293],[1971,.291],
]);

// HomeSafe Intro (first-time borrowers).
const HS_INTRO=byBirthYear([
  [1930,.611],[1938,.610],[1939,.608],[1940,.606],[1941,.603],[1942,.600],[1943,.590],[1944,.581],
  [1945,.569],[1946,.556],[1947,.544],[1948,.534],[1949,.524],[1950,.516],[1951,.508],[1952,.497],
  [1953,.487],[1954,.477],[1955,.468],[1956,.460],[1957,.453],[1958,.446],[1959,.440],[1960,.435],
  [1961,.429],[1962,.424],[1963,.419],[1964,.414],[1965,.410],[1966,.407],[1967,.404],[1968,.401],
  [1969,.397],[1970,.395],[1971,.393],
]);

// Longbridge Platinum A 5.0 (8.49%, 4% orig). Platinum "Max LTV" is exactly
// this +0.020 at every age — that is the calculator's "LTV +2%" option.
const PLAT_A=byBirthYear([
  [1931,.551],[1938,.550],[1939,.548],[1940,.546],[1941,.543],[1942,.540],[1943,.530],[1944,.521],
  [1945,.509],[1946,.496],[1947,.484],[1948,.474],[1949,.464],[1950,.456],[1951,.448],[1952,.437],
  [1953,.427],[1954,.417],[1955,.408],[1956,.400],[1957,.393],[1958,.386],[1959,.380],[1960,.375],
  [1961,.369],[1962,.364],[1963,.359],[1964,.354],[1965,.350],[1966,.347],[1967,.344],[1968,.341],
  [1969,.337],[1970,.335],[1971,.333],
]);

// Platinum 5.0 Max LTV = Platinum A + 0.020 at every age (confirmed by Reece);
// SmartFi Choice Fixed Max (9.48%, 4% orig) lines up with it.
const PLAT_MAX_LTV={};Object.keys(PLAT_A).forEach(function(a){PLAT_MAX_LTV[a]=+(PLAT_A[a]+0.02).toFixed(3);});

// HomeSafe 9.48 (Standard Tier 3, high LTV, 2% orig).
const HS_948=byBirthYear([
  [1938,.585],[1939,.583],[1940,.582],[1941,.578],[1942,.575],[1943,.565],[1944,.557],[1945,.544],
  [1946,.531],[1947,.519],[1948,.509],[1949,.498],[1950,.491],[1951,.482],[1952,.472],[1953,.462],
  [1954,.452],[1955,.443],[1956,.435],[1957,.427],[1958,.420],[1959,.414],[1960,.408],[1961,.403],
  [1962,.397],[1963,.393],[1964,.388],[1965,.384],[1966,.382],[1967,.379],[1968,.375],[1969,.371],
  [1970,.369],[1971,.367],
]);

// HomeSafe Second — the most it can be WITH NO FIRST LIEN, as a share of
// home value (FOA software, 10/1/26). 80+ .25 · 62–79 .20 · 56–61 .15.
// How an existing first lien reduces it is NOT known yet, so the calculator
// uses this as a ceiling only.
export const HS2_NO_LIEN=byBirthYear([[1935,.25],[1947,.25],[1948,.20],[1965,.20],[1966,.15],[1971,.15]]);

// HomeSafe Standard Tier 2 (9.45 @ 0%) — DERIVED, not read: FOA's HomeSafe
// manual (p7, rev. Sept 2026) says Intro "offers a 5% LTV higher than the
// HomeSafe Standard Tier 2", so Tier 2 = the Intro table − .050 at every age.
// Confirm one age in the FOA software when convenient.
const HS_STD_T2={};Object.keys(HS_INTRO).forEach(function(a){HS_STD_T2[a]=+(HS_INTRO[a]-0.05).toFixed(3);});

// The day every table above was read off the lenders' software. A table
// re-read later gets ITS read date as readOn (see FOA_LTV_CUT below — a FOA
// table read on/after 10/3/26 already carries the Bulletin 33 cut).
const TABLES_READ_ON="2026-10-01";

// key = rate-sheet key; each entry = one TIER (rate + orig %) as read. A tier
// is matched on its orig % (tiers with different orig are different LTV
// products), and on rate within MAX_RATE_DRIFT.
//   singleLtv — the product has ONE LTV at every rate/orig pair (HomeSafe
//               Intro: FOA lists it as "Intro: Highest LTV" with three
//               rate/origination buy-downs), so every tier uses the table
//               as-is, with no rate scaling.
//   derived   — built from a documented rule, not read off the software.
//   readOn    — the local date (YYYY-MM-DD) the table was read; a DERIVED
//               table carries its source's date (Tier 2 = the Intro read).
export const PLF_TABLES={
  foaHSStd:[{rate:8.49,orig:1,byAge:LOW_FIXED,label:"FOA table",readOn:TABLES_READ_ON},{rate:9.45,orig:0,byAge:HS_STD_T2,label:"FOA rule: Intro − 5% LTV",derived:true,readOn:TABLES_READ_ON},{rate:9.48,orig:2,byAge:HS_948,label:"FOA table",readOn:TABLES_READ_ON}],
  smartChoiceFixedSaver:[{rate:8.39,orig:1,byAge:LOW_FIXED,label:"SmartFi table",readOn:TABLES_READ_ON}],
  smartChoiceFixedMax:[{rate:9.48,orig:4,byAge:PLAT_MAX_LTV,label:"SmartFi table (= Platinum Max LTV)",readOn:TABLES_READ_ON}],
  foaHSIntro:[{rate:9.49,orig:4,byAge:HS_INTRO,label:"FOA Intro table",singleLtv:true,readOn:TABLES_READ_ON}],
  lbPlatinumFixed:[{rate:8.49,orig:4,byAge:PLAT_A,label:"Longbridge Platinum A table",readOn:TABLES_READ_ON}],
};

// ── FOA HomeSafe LTV cut — Wholesale Bulletin 33-2026 (10/2/26) ──
// From 10/3/26 every NEW HomeSafe application prices 3% lower LTV on
// HomeSafe Standard (all tiers), HomeSafe Intro and HomeSafe Select Intro
// (their LESA versions included). Pipeline loans (application taken before
// 10/3) keep the existing LTVs and must close by 10/31/26. HomeSafe Second
// is NOT in it (and New York tiers aren't modelled here).
// Reece, 10/2/26: the 3% is 3 PERCENTAGE POINTS off the PLF (HomeSafe Std
// 8.49 at 82: .529 → .499) — the same convention as Platinum's "LTV +2%"
// (+0.020). The cut keys off the SHEET key, never the table object:
// LOW_FIXED is shared with SmartFi Choice Fixed Saver, which isn't cut.
//   effective   — first quote date the cut applies to
//   oldLtvUntil — last date the deal builder's "application taken before
//                 10/3 — keep old LTVs" switch is offered / honoured
// A table read off FOA's software on/after `effective` already has the cut
// in it, so it is never cut again (readOn ≥ effective); the generic estimate
// and the 10/1 envelope tables are pre-cut numbers and are cut.
export const FOA_LTV_CUT={points:0.03,effective:"2026-10-03",oldLtvUntil:"2026-10-31",source:"FOA Bulletin 33-2026",
  keys:["foaHSStd","foaHSIntro","foaHSSelIntro"],
  label:"−3 pts LTV (FOA Bulletin 33, apps from 10/3/26)",
  ruleCut:"FOA Bulletin 33 −3 pts",ruleOld:"pre-10/3 LTVs"};

// The quote date: the browser-LOCAL calendar day as YYYY-MM-DD (never
// toISOString().slice(0,10) — that is tomorrow's date after ~5pm Pacific).
// Takes a Date, a "YYYY-MM-DD" (returned as is), a date-time string, or
// nothing (today).
export function quoteDate(d){
  if(typeof d==="string"&&/^\d{4}-\d{2}-\d{2}$/.test(d))return d;
  var t=d==null?new Date():new Date(d);
  if(isNaN(t.getTime()))t=new Date();
  return t.getFullYear()+"-"+String(t.getMonth()+1).padStart(2,"0")+"-"+String(t.getDate()).padStart(2,"0");
}
// Is this sheet key one Bulletin 33 cuts, on this quote date?
export function foaLtvCutInEffect(key,asOf){
  return FOA_LTV_CUT.keys.indexOf(key)>=0&&quoteDate(asOf)>=FOA_LTV_CUT.effective;
}
// The deal builder's "application taken before 10/3 — keep old LTVs" switch
// is offered (and a stored one honoured) 10/3/26 through 10/31/26 only.
export function oldLtvSwitchOpen(asOf){
  var d=quoteDate(asOf);
  return d>=FOA_LTV_CUT.effective&&d<=FOA_LTV_CUT.oldLtvUntil;
}
// The cut for one PLF: {cut, cutEligible, oldLtv, ltvRule}. `preCut` = the
// number came from a table read before the effective date (or one derived
// from such a table), or from the estimate / envelope path.
function ltvCutFor(key,preCut,opts){
  var o=opts||{};
  var fam=FOA_LTV_CUT.keys.indexOf(key)>=0;
  if(!fam)return{cut:0,cutEligible:false,oldLtv:false,ltvRule:null};
  var d=quoteDate(o.asOf);
  if(d<FOA_LTV_CUT.effective)return{cut:0,cutEligible:false,oldLtv:false,ltvRule:FOA_LTV_CUT.ruleOld};
  if(!preCut)return{cut:0,cutEligible:false,oldLtv:false,ltvRule:FOA_LTV_CUT.ruleCut};   // the table already has it
  var old=!!o.oldLtv&&d<=FOA_LTV_CUT.oldLtvUntil;
  return{cut:old?0:FOA_LTV_CUT.points,cutEligible:true,oldLtv:old,ltvRule:old?FOA_LTV_CUT.ruleOld:FOA_LTV_CUT.ruleCut};
}
function readBefore(readOn){return !readOn||String(readOn)<FOA_LTV_CUT.effective;}

// The highest-LTV table each product family has. An ESTIMATED tier is capped
// at it: a tier with no table of its own can never out-borrow the lender's
// own max-LTV tier at the same age (before this, the generic curve put
// estimated tiers above the tabled max-LTV tiers from about age 70 up, and
// the estimate became the recorded best quote). Keys without a family table
// are capped at the highest lender table on file.
const ENVELOPES={
  foaHSStd:{byAge:HS_948,label:"FOA Tier 3 (max-LTV) table",readOn:TABLES_READ_ON},
  foaHSIntro:{byAge:HS_INTRO,label:"FOA Intro table",readOn:TABLES_READ_ON},
  foaHSSelIntro:{byAge:HS_INTRO,label:"FOA Intro (highest FOA) table",readOn:TABLES_READ_ON},
  smartChoiceFixedSaver:{byAge:PLAT_MAX_LTV,label:"SmartFi Choice Max table"},
  smartChoiceFixedMax:{byAge:PLAT_MAX_LTV,label:"SmartFi Choice Max table"},
  smartChoiceFixedPro:{byAge:PLAT_MAX_LTV,label:"SmartFi Choice Max table"},
  smartChoiceFixedCore:{byAge:PLAT_MAX_LTV,label:"SmartFi Choice Max table"},
  smartChoiceFlex:{byAge:PLAT_MAX_LTV,label:"SmartFi Choice Max table"},
  lbPlatinumFixed:{byAge:PLAT_MAX_LTV,label:"Platinum Max LTV table"},
  lbPlatinumLOC:{byAge:PLAT_MAX_LTV,label:"Platinum Max LTV table"},
};
const ALL_TABLES=[LOW_FIXED,HS_INTRO,PLAT_A,PLAT_MAX_LTV,HS_948];
// The PLF ceiling for an estimated tier of `key` at this nearest age.
export function plfEnvelope(key,age){
  const e=ENVELOPES[key];
  if(e)return{plf:ageLookup(e.byAge,age),label:e.label,readOn:e.readOn||null};
  let best=null;
  ALL_TABLES.forEach(function(t){const v=ageLookup(t,age);if(v!=null&&(best==null||v>best))best=v;});
  return{plf:best,label:"highest lender table on file",readOn:TABLES_READ_ON};
}

// Generic proprietary curve — the fallback for tiers with no lender table.
// Rises a point a year from .30 at 55 to .67 at 90, minus 1.5 points per
// point of note rate over 7%. Only ever used through proprietaryPLF, which
// caps it at the family's max-LTV table.
export function jumboPLF(borrowerAge,noteRate){
  const b={55:.30,56:.31,57:.32,58:.33,59:.34,60:.36,61:.37,62:.39,63:.40,64:.41,65:.42,66:.43,67:.44,68:.45,69:.46,70:.47,71:.48,72:.49,73:.50,74:.51,75:.52,76:.53,77:.54,78:.55,79:.56,80:.57,81:.58,82:.59,83:.60,84:.61,85:.62,86:.63,87:.64,88:.65,89:.66,90:.67};
  const a=Math.min(Math.max(Math.round(borrowerAge),55),90);const base=b[a]||.49;const adj=Math.max(0,(noteRate-7)*.015);
  return Math.max(.20,base-adj);
}

// Age at the nearest birthday (what lenders price on).
export function nearestAge(dob,today){
  const d=new Date(String(dob||"").slice(0,10)+"T12:00:00");
  if(isNaN(d))return 0;
  const t=today?new Date(today):new Date();
  return Math.round((t-d)/(365.2425*864e5));
}

// Reece's rule: a skipped birth year has the SAME PLF as the listed year
// before it (e.g. Intro 1930 .611 holds for 1931–1937, 1938 is .610). In age
// terms a gap takes the OLDER listed age's value. Flat past either end.
export function ageLookup(byAge,age){
  const ages=Object.keys(byAge).map(Number).sort(function(a,b){return a-b;});
  if(!ages.length)return null;
  if(age<=ages[0])return byAge[ages[0]];
  if(age>=ages[ages.length-1])return byAge[ages[ages.length-1]];
  for(let i=0;i<ages.length-1;i++){
    const a=ages[i],b=ages[i+1];
    if(age===a)return byAge[a];
    if(age>a&&age<=b)return byAge[b];
  }
  return null;
}

// PLF for a sheet key + tier rate at this nearest age.
// Returns {plf, source:"table"|"derived"|"estimate", label, baseLabel,
// capped, cut, cutEligible, oldLtv, ltvRule}.
// `estimate(age,rate)` is the generic curve, used for the rate-drift scaling
// and as the fallback; the fallback is capped at the family's max-LTV table.
// `opts` {asOf, oldLtv} — the quote date (default: today, local) and the deal
// builder's "application taken before 10/3 — keep old LTVs" switch — decide
// FOA's Bulletin 33 cut (FOA_LTV_CUT), taken off AFTER the rate-drift
// scaling and the envelope cap, never below 0:
//   cut         — points taken off (0.03, or 0)
//   cutEligible — the cut is in effect for this key and number (the old-LTV
//                 switch means something on this card)
//   oldLtv      — the switch kept the old LTV (in its window, on a cut card)
//   ltvRule     — which LTVs priced it ("FOA Bulletin 33 −3 pts" /
//                 "pre-10/3 LTVs"); null outside the three HomeSafe families
export function proprietaryPLF(key,age,rate,estimate,orig,opts){
  const list=PLF_TABLES[key]||[];
  let best=null;
  list.forEach(function(e){
    if(e.singleLtv){if(!best)best=e;return;}                        // one LTV for every tier
    if(e.orig!=null&&orig!=null&&Math.abs(e.orig-orig)>1e-9)return; // different tier
    const dr=Math.abs(e.rate-rate);if(dr<=MAX_RATE_DRIFT&&(!best||best.singleLtv||dr<Math.abs(best.rate-rate)))best=e;
  });
  const withCut=function(r,lc){
    r.cut=lc.cut;r.cutEligible=lc.cutEligible;r.oldLtv=lc.oldLtv;r.ltvRule=lc.ltvRule;r.baseLabel=r.label;
    if(lc.cut>0)r.label=(r.label?r.label+" · ":"")+FOA_LTV_CUT.label;
    return r;
  };
  if(!best){
    // The estimate is a pre-cut number; the envelope is cut only when it
    // was read before the cut (a post-cut envelope already has it).
    const lc=ltvCutFor(key,true,opts);
    let est=estimate(age,rate);let capped=false;
    if(lc.cut>0)est=Math.max(0,est-lc.cut);
    const env=plfEnvelope(key,age);
    if(env.plf!=null){
      const envCut=readBefore(env.readOn)?lc.cut:0;
      const cap=envCut>0?Math.max(0,env.plf-envCut):env.plf;
      if(est>cap){est=cap;capped=true;}
    }
    return withCut({plf:est,source:"estimate",label:capped?"capped at the "+env.label:"",capped:capped},lc);
  }
  let plf=ageLookup(best.byAge,age);
  const drift=!best.singleLtv&&Math.abs(best.rate-rate)>1e-9;
  if(drift){const a=estimate(age,rate),b=estimate(age,best.rate);if(b>0)plf=plf*a/b;}
  const lc=ltvCutFor(key,readBefore(best.readOn),opts);
  if(lc.cut>0)plf=Math.max(0,plf-lc.cut);
  return withCut({plf:plf,source:best.derived?"derived":"table",label:best.label+(drift?" (scaled from "+best.rate+"%)":""),capped:false},lc);
}

// ── LESA — HUD's published formula (reproduces all 28 lender-software runs to the cent) ──
// Source: HUD "HECM Financial Assessment and Property Charge Guide" (rev. 7-13-16,
// via ML 2016-10; same formula since ML 2014-22), Section 5.3:
//   Projected Life Expectancy Property Charge Cost
//     = (1.2 × PC ÷ 12) × [(1+c)^(m+1) − (1+c)] ÷ [c × (1+c)^m]
//   PC = annual property taxes + homeowners insurance + flood insurance
//   1.2 = HUD's allowance for those charges rising over time
//   c   = (expected rate + annual MIP 0.5%) ÷ 12 — monthly compounding
//         (expected rate = margin + 10-yr CMT, at the nearest 1/8, as the software shows)
//   m   = 12 × TALC life expectancy of the youngest borrower (Reg Z, 12 CFR 1026
//         Appendix L, "Loan Period 2"), age rounded UP if the next birthday is
//         less than 183 days after closing.
// Proprietary lenders (FOA HomeSafe, MoO SecureEquity, Longbridge Platinum) use
// the same inputs with no growth: LESA = 1.2 × PC × TALC years (72 → 15.6×,
// 77 → 12.0×, 86 → 7.2× — matches Reece's runs at all three). SmartFi: no LESA.
// Verified against Reece's software 10/1/26: ages 62/67/72/77/86, six margins.
export const TALC_YEARS={62:21,63:20,64:19,65:18,66:18,67:17,68:16,69:16,70:15,71:14,72:13,73:13,74:12,75:12,76:11,77:10,78:10,79:9,80:9,81:8,82:8,83:7,84:7,85:6,86:6,87:6,88:5,89:5,90:5,91:4,92:4,93:4,94:4,95:3};
// HUD's table starts at 62 (HECM minimum). Proprietary goes to 55; below 62 we
// extend it one year per year of age (61 → 22 … 55 → 28) — ESTIMATED, unverified.
export function talcYears(age){
  const a=Math.round(age);
  if(a>=95)return 3;
  if(a<62)return 21+(62-Math.max(55,a));
  return TALC_YEARS[a];
}
// HUD age rule: age at closing, +1 if the next birthday is under 183 days away.
export function talcAge(dob,asOf){
  const d=new Date(String(dob||"").slice(0,10)+"T12:00:00");if(isNaN(d))return 0;
  const t=asOf?new Date(asOf):new Date();
  let age=t.getFullYear()-d.getFullYear();
  let next=new Date(t.getFullYear(),d.getMonth(),d.getDate(),12);
  if(next<=t){next=new Date(t.getFullYear()+1,d.getMonth(),d.getDate(),12);}else{age--;}
  const daysToNext=(next-t)/864e5;
  return daysToNext<183?age+1:age;
}
// Kept for the calculator panel: the horizon in years.
export function hecmLesaHorizon(age){return talcYears(age);}
// LESA = (annual T&I ÷ 12) × hecmLesaFactor(age, expectedRate) — includes HUD's 1.2.
export function hecmLesaFactor(age,expRate){
  const e=Math.round(expRate*8)/8;
  const c=(e+0.5)/1200;
  const m=12*talcYears(age);
  return 1.2*(Math.pow(1+c,m+1)-(1+c))/(c*Math.pow(1+c,m));
}
// Proprietary: LESA = annual T&I × propLesaYears(age).
export function propLesaYears(age){return 1.2*talcYears(age);}
